import { useState } from "react";
import { format } from "date-fns";
import Modal from "../common/Modal";
import PaginationComponent from "../PaginationComponent";
import Loading from "../common/LoaderCommon";
import { useGetVendorPaymentHistory, useDownloadVendorPaymentHistory } from "../../service/vendorService";

interface Props {
  vendorId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function VendorPaymentHistoryModal({ vendorId, open, setOpen }: Props) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data, isLoading } = useGetVendorPaymentHistory({
    vendorId: vendorId.toString(),
    search: "",
    pageNo: currentPage - 1,
    pageSize,
  });

  const paymentData = data?.content || [];
  const totalPages = data?.totalPages || 1;

  const { mutate: downloadHistory, isPending: isDownloading } = useDownloadVendorPaymentHistory();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount);
  };

  const formatPaymentDate = (dateStr: string) => {
    if (!dateStr) return "-";
    const dateObj = new Date(dateStr);
    if (!isNaN(dateObj.getTime())) {
      return format(dateObj, "dd MMM yyyy");
    }
    return dateStr;
  };

  return (
    <Modal open={open} setOpen={setOpen} className="max-w-4xl p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-[#21314A]">
          Vendor Payment History
        </h2>
        <p className="text-sm text-[#7D8998] mt-1">
          Review all payments made to this vendor.
        </p>
      </div>

      <div className="rounded-xl border border-[#EEF2FA] bg-white overflow-hidden">
        {isLoading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <Loading />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-[#F5F8FD] text-left text-sm font-semibold text-[#31425E]">
                    <th className="px-6 py-4">Payment No.</th>
                    <th className="px-6 py-4">Purchase Ref</th>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Mode</th>
                    <th className="px-6 py-4 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentData.length > 0 ? (
                    paymentData.map((item) => (
                      <tr
                        key={item.id}
                        className="border-t border-[#EEF2FA] text-sm text-[#43536A] transition-colors hover:bg-[#FAFCFF]"
                      >
                        <td className="px-6 py-4 font-medium text-[#24344F]">
                          {item.paymentNumber || "-"}
                        </td>
                        <td className="px-6 py-4">{item.purchaseNumber || "-"}</td>
                        <td className="px-6 py-4">
                          {formatPaymentDate(item.paymentDate)}
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center rounded-full bg-[#F3F4F3] px-2.5 py-1 text-xs font-medium text-[#43536A]">
                            {item.paymentMode}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-semibold text-[#1A5EF9]">
                          {formatCurrency(item.amount)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-12 text-center text-sm text-[#7D8998]"
                      >
                        No payment history found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="border-t border-[#EEF2FA] bg-white px-6 py-4">
                <PaginationComponent
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  fetchNextPage={() => {
                    if (currentPage < totalPages) {
                      setCurrentPage((prev) => prev + 1);
                    }
                  }}
                  fetchPreviousPage={() => {
                    if (currentPage > 1) {
                      setCurrentPage((prev) => prev - 1);
                    }
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={() => downloadHistory(vendorId)}
          disabled={isDownloading || paymentData.length === 0}
          className="rounded-[10px] border border-[#EEF2FA] bg-white px-6 py-2.5 text-sm font-semibold text-[#43536A] transition-colors hover:bg-[#F5F8FD] disabled:opacity-50"
        >
          {isDownloading ? "Downloading..." : "Download Excel"}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="rounded-[10px] bg-[#1A5EF9] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#144BD9]"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
